/**
 * API Service - Centralized HTTP utility for communicating with the Django backend.
 * All API calls go through this file. When the database is connected later,
 * no changes will be needed here - the backend endpoints stay the same.
 */

const API_BASE = '/api';

// Simple in-memory cache to prevent redundant fetches
const apiCache = new Map();

export function clearApiCache() {
  apiCache.clear();
}

async function request(endpoint, options = {}) {
  const isCacheable = (options.method === 'GET' || !options.method) && options.cache !== false;
  const cacheKey = endpoint;

  if (isCacheable && apiCache.has(cacheKey)) {
    return apiCache.get(cacheKey);
  }

  // Invalidate cache on mutations (POST, PUT, DELETE, PATCH)
  if (!isCacheable) {
    apiCache.clear();
  }

  const url = `${API_BASE}${endpoint}`;
  const { headers = {}, cache, ...restOptions } = options;

  const resolvedHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Clean or sanitize Authorization header
  if (resolvedHeaders.Authorization) {
    const authVal = String(resolvedHeaders.Authorization).trim();
    if (
      authVal === 'Bearer' ||
      authVal === 'Bearer true' ||
      authVal === 'Bearer false' ||
      authVal === 'Bearer null' ||
      authVal === 'Bearer undefined' ||
      authVal.includes('undefined') ||
      authVal.includes('null') ||
      !authVal.startsWith('Bearer ') ||
      authVal.split(' ')[1]?.length < 15
    ) {
      delete resolvedHeaders.Authorization;
    }
  }

  // If no valid Authorization header yet, check localStorage for a real JWT token
  if (!resolvedHeaders.Authorization) {
    const candidateTokens = [
      localStorage.getItem('employer_token'),
      localStorage.getItem('token'),
      localStorage.getItem('staff_token'),
      localStorage.getItem('access_token')
    ];
    for (const cand of candidateTokens) {
      if (cand && typeof cand === 'string' && cand.length > 20 && cand.includes('.') && cand !== 'true' && cand !== 'false') {
        resolvedHeaders.Authorization = `Bearer ${cand}`;
        break;
      }
    }
  }

  const config = {
    method: options.method || 'GET',
    credentials: options.credentials || 'include',
    headers: resolvedHeaders,
    ...restOptions,
  };

  if (cache === false) {
    config.cache = 'no-store';
  } else if (cache) {
    config.cache = cache;
  }

  try {
    const response = await fetch(url, config);
    const contentType = response.headers.get('content-type');
    let data = null;
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      // It's HTML or plain text (likely a server-level crash or gateway timeout)
      const text = await response.text();
      console.warn(`[API] Received non-JSON response from ${endpoint}:`, text.substring(0, 100));
      data = { 
        error: 'Backend Communication Error', 
        message: `Server returned ${response.status} (${response.statusText}).`,
        is_html: true 
      };
    }

    const result = { data, ok: response.ok, status: response.status };
    
    // Cache successful GET requests
    if (isCacheable && response.ok) {
      apiCache.set(cacheKey, result);
    }

    return result;
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') console.error(`API Error [${endpoint}]:`, error);
    return { data: null, ok: false, status: 0, error };
  }
}

// ── Direct-to-S3 upload (presigned POST) ────────────────────────────────
// Uploads a browser File straight to S3 - the bytes never pass through our
// server. Returns the storage key to persist, or null if direct upload isn't
// available (caller can fall back to base64). Requires the bucket CORS rule.
export async function uploadDocumentToS3(file, kind = 'documents') {
  if (!file) return null;
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds the 5MB maximum limit.`);
  }

  let presign;
  try {
    presign = await request('/uploads/presign/', {
      method: 'POST',
      body: JSON.stringify({
        kind,
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
      }),
    });
  } catch (err) {
    return null;
  }
  if (!presign || presign.status === 409 || !presign.ok || !presign.data?.url) return null;

  const { key, url, fields } = presign.data;
  const form = new FormData();
  Object.entries(fields || {}).forEach(([k, v]) => form.append(k, v));
  form.append('file', file); // file part MUST be last

  try {
    const s3Res = await fetch(url, { method: 'POST', body: form });
    if (!(s3Res.status === 204 || s3Res.status === 201 || s3Res.ok)) {
      return null;
    }
    return key;
  } catch (err) {
    return null;
  }
}

// ==================== HOME ====================
export const homeAPI = {
  getHero: () => request('/home/hero/'),
  getServices: () => request('/home/services/'),
  getTestimonials: () => request('/home/testimonials/'),
  getCommunityStats: () => request('/home/community-stats/'),
  getPopularPanels: () => request('/home/popular-panels/'),
  getOffers: () => request('/offers/all/', { cache: false }),
  subscribeNewsletter: (email) =>
    request('/home/newsletter/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
};

// ==================== CATALOG ====================
export const catalogAPI = {
  getCategories: () => request('/catalog/categories/'),
  getSampleMatrices: () => request('/superadmin/catalog/sample-matrices/'),
  getTurnaroundTimes: () => request('/superadmin/catalog/turnaround-times/'),
  getTests: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const res = await request(`/catalog/tests/${query ? '?' + query : ''}`);
    if (res.ok && Array.isArray(res.data)) {
      res.data = res.data.filter(t => !t.title.toLowerCase().includes('mobile phlebotomy'));
    }
    return res;
  },
  getTestById: (id, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/catalog/tests/${id}/${query ? '?' + query : ''}`);
  },
  submitInquiry: (payload) => request('/patients/support/contact/', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),
};

// ==================== OFFERS ====================
export const offersAPI = {
  getOffers: (params = {}) => {
    const cleanParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'All') {
        cleanParams.append(key, value);
      }
    });
    const query = cleanParams.toString();
    return request(`/offers/all/${query ? '?' + query : ''}`, { cache: false });
  },
  getOfferById: (id) => request(`/offers/${id}/`, { cache: false }),
  // Featured "card groups" with real-time min price + max discount % per card.
  getCards: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return request(`/offers/cards/${query ? '?' + query : ''}`);
  },
  getPhlebotomyOffers: () => request('/offers/phlebotomy/'),
  getCategories: () => request('/superadmin/offers/categories/'),
  getTypes: () => request('/superadmin/offers/types/'),
};

// ==================== BOOKINGS ====================
export const bookingsAPI = {
  createBooking: (bookingData) =>
    request('/bookings/', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    }),
  getBookableTests: () => request('/bookings/tests/'),
};

// ==================== PRICING ====================
export const pricingAPI = {
  preview: (data) =>
    request('/pricing/preview/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ==================== PAYMENTS ====================
export const paymentsAPI = {
  createPaymentIntent: (data) =>
    request('/payments/create-payment-intent/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  createCheckoutSession: (data) =>
    request('/payments/create-checkout-session/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  verifyCheckoutSession: (data) =>
    request('/payments/verify-checkout-session/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

// ==================== EMPLOYERS ====================
const authHdr = (token) => (token && typeof token === 'string' && token.length > 20 && token !== 'true' && token !== 'false' ? { Authorization: `Bearer ${token}` } : {});

export const employersAPI = {
  getPlans: () => request('/employers/plans/'),
  getComparison: () => request('/employers/comparison/'),
  signup: (data) => request('/employers/signup/', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  getStats: (token) => request('/employers/dashboard/stats/', {
    headers: authHdr(token)
  }),
  getEmployees: (token) => request('/employers/employees/', {
    headers: authHdr(token)
  }),
  addEmployee: (employeeData, token) => request('/employers/employees/', {
    method: 'POST',
    headers: authHdr(token),
    body: JSON.stringify(employeeData)
  }),
  deleteEmployee: (id, token) => request(`/employers/employees/${id}/`, {
    method: 'DELETE',
    headers: authHdr(token)
  }),
  submitOnsiteRequest: (formData, token) => request('/employers/onsite/', {
    method: 'POST',
    headers: authHdr(token),
    body: JSON.stringify(formData)
  }),
  getOnsiteRequests: (token) => request('/employers/onsite/', {
    headers: authHdr(token)
  }),
  getEmployerPlansCatalog: () => request('/superadmin/employer-plans/'),
  getBilling: (token) => request('/employers/billing/', {
    headers: authHdr(token)
  }),
  selectPlan: (planId, token) => request('/employers/select-plan/', {
    method: 'POST',
    headers: authHdr(token),
    body: JSON.stringify({ plan_id: planId })
  }),
  sendInviteEmail: (employeeId, token) => request(`/employers/employees/${employeeId}/send-email/`, {
    method: 'POST',
    headers: authHdr(token)
  }),
  remindEmployee: (employeeId, token) => request(`/employers/employees/${employeeId}/remind/`, {
    method: 'POST',
    headers: authHdr(token)
  }),
  redeemCredit: (data, token) => request('/employers/credits/redeem/', {
    method: 'POST',
    headers: authHdr(token),
    body: JSON.stringify(data)
  }),
  verifyEnrollment: (token) => request(`/employers/enroll/verify/${token}/`),
  completeEnrollment: (token, data) => request(`/employers/enroll/complete/${token}/`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  getProfile: (token) => request('/employers/profile/', {
    headers: authHdr(token)
  }),
  updateProfile: (data, token) => request('/employers/profile/', {
    method: 'POST',
    headers: authHdr(token),
    body: JSON.stringify(data)
  }),

  // HR Admin (Internal)
  getAdminStats: (token) => request('/employers/admin/stats/', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getAdminAccounts: (params = {}, token) => {
    const query = new URLSearchParams(params).toString();
    return request(`/employers/admin/accounts/?${query}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  updateAccountStatus: (id, status, reason, token) => request(`/employers/admin/accounts/${id}/status/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ status, reason })
  }),
  getAccountDetails: (id, token) => request(`/employers/admin/accounts/${id}/details/`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  getAdminPlans: (token) => request('/employers/admin/plans/', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  changeEmployerPlan: (id, planName, reason, renewalDate, token) => request(`/employers/admin/accounts/${id}/change-plan/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ plan_name: planName, reason, renewal_date: renewalDate })
  }),
  getSubsidies: (params = {}, token) => {
    const query = new URLSearchParams(params).toString();
    return request(`/employers/admin/subsidies/?${query}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  updateSubsidyStatus: (id, status, reason, token) => request(`/employers/admin/subsidies/${id}/status/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ status, reason })
  }),
  getCreditRules: (token) => request('/employers/admin/credits/rules/', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  updateCreditRules: (rulesData, token) => request('/employers/admin/credits/rules/', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(rulesData)
  }),
  getCreditDisputes: (token) => request('/employers/admin/credits/disputes/', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  resolveCreditDispute: (id, action, amount, reason, token) => request(`/employers/admin/credits/disputes/${id}/action/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action, adjustment_amount: amount, reason })
  }),
  getAdminReports: (params = {}, token) => {
    const query = new URLSearchParams(params).toString();
    return request(`/employers/admin/reports/?${query}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  getDueTesting: (params = {}, token) => {
    const query = new URLSearchParams(params).toString();
    return request(`/employers/admin/due-testing/?${query}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  },
  getAdminOnsite: (token) => request('/employers/admin/onsite/', {
    headers: { Authorization: `Bearer ${token}` }
  }),
  updateAdminOnsite: (id, data, token) => request(`/employers/admin/onsite/${id}/status/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: JSON.stringify(data)
  }),
  getAdminActivity: (token) => request('/employers/admin/activity/', {
    headers: { Authorization: `Bearer ${token}` }
  }),
};

// ==================== RESEARCH ====================
export const researchAPI = {
  getStats: () => request('/research/stats/'),
  getServices: () => request('/research/services/'),
  getBiorepository: () => request('/research/biorepository/'),
  getCollaborations: () => request('/research/collaborations/'),
  submitQuote: (quoteData) =>
    request('/research/quote/', {
      method: 'POST',
      body: JSON.stringify(quoteData),
    }),
  subscribeNewsletter: (email) =>
    request('/research/newsletter/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  submitValidation: (data) =>
    request('/research/validation/submit/', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getValidationTracker: () => request('/research/validation/tracker/'),
};

// ==================== DIAGNOSTIC PANEL ====================
export const diagnosticAPI = {
  getTasks: (projectId) => request(`/research/diag/tasks/${projectId ? '?project_id=' + projectId : ''}`),
  createTask: (data) => request('/research/diag/tasks/', { method: 'POST', body: JSON.stringify(data) }),
  
  getMessages: (projectId) => request(`/research/diag/messages/${projectId ? '?project_id=' + projectId : ''}`),
  sendMessage: (data) => request('/research/diag/messages/', { method: 'POST', body: JSON.stringify(data) }),
  
  getDocuments: (projectId) => request(`/research/diag/documents/${projectId ? '?project_id=' + projectId : ''}`),
  uploadDocument: (data) => request('/research/diag/documents/', { method: 'POST', body: JSON.stringify(data) }),
  
  getInvoices: (projectId) => request(`/research/diag/invoices/${projectId ? '?project_id=' + projectId : ''}`),
  createInvoice: (data) => request('/research/diag/invoices/', { method: 'POST', body: JSON.stringify(data) }),
  
  getPipeline: () => request('/research/diag/pipeline/'),
  updatePipeline: (data) => request('/research/diag/pipeline/', { method: 'PATCH', body: JSON.stringify(data) }),
};

// ==================== SUPER ADMIN ====================
export const superAdminAPI = {
  // Catalog Mgt
  getCatalogBootstrap: () => request('/superadmin/catalog/bootstrap/'),
  getAdminTests: () => request('/superadmin/catalog/tests/'),
  createTest: (data) => request('/superadmin/catalog/tests/create/', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  bulkImportTests: (data) => request('/superadmin/catalog/tests/bulk-import/', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updateTest: (id, data) => request(`/superadmin/catalog/tests/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deleteTest: (id) => request(`/superadmin/catalog/tests/${id}/delete/`, {
    method: 'DELETE'
  }),
  toggleTest: (id) => request(`/superadmin/catalog/tests/${id}/toggle/`, {
    method: 'PATCH'
  }),

  // Phlebotomy Offers Mgt
  getAdminPhlebOffers: () => request('/superadmin/phlebotomy-offers/'),
  createPhlebOffer: (data) => request('/superadmin/phlebotomy-offers/create/', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  updatePhlebOffer: (id, data) => request(`/superadmin/phlebotomy-offers/${id}/`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  deletePhlebOffer: (id) => request(`/superadmin/phlebotomy-offers/${id}/delete/`, {
    method: 'DELETE'
  }),
  togglePhlebOffer: (id) => request(`/superadmin/phlebotomy-offers/${id}/toggle/`, {
    method: 'PATCH'
  }),

  // Analytical Categories Mgt
  getCatalogCategories: () => request('/superadmin/catalog/categories/'),
  createCatalogCategory: (name) => request('/superadmin/catalog/categories/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteCatalogCategory: (id) => request(`/superadmin/catalog/categories/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Sample Matrices Mgt
  getSampleMatrices: () => request('/superadmin/catalog/sample-matrices/'),
  createSampleMatrix: (name) => request('/superadmin/catalog/sample-matrices/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteSampleMatrix: (id) => request(`/superadmin/catalog/sample-matrices/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Turnaround Times Mgt
  getTurnaroundTimes: () => request('/superadmin/catalog/turnaround-times/'),
  createTurnaroundTime: (name) => request('/superadmin/catalog/turnaround-times/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteTurnaroundTime: (id) => request(`/superadmin/catalog/turnaround-times/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Tube Types Mgt
  getTubeTypes: () => request('/superadmin/catalog/tube-types/'),
  createTubeType: (name) => request('/superadmin/catalog/tube-types/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteTubeType: (id) => request(`/superadmin/catalog/tube-types/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Biomarkers Taxonomy Mgt
  getCatalogBiomarkers: () => request('/superadmin/catalog/biomarkers/'),
  createCatalogBiomarker: (name) => request('/superadmin/catalog/biomarkers/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteCatalogBiomarker: (id) => request(`/superadmin/catalog/biomarkers/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Biomarker Units Taxonomy Mgt
  getBiomarkerUnits: () => request('/superadmin/catalog/biomarker-units/'),
  createBiomarkerUnit: (name) => request('/superadmin/catalog/biomarker-units/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteBiomarkerUnit: (id) => request(`/superadmin/catalog/biomarker-units/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Subcategories Mgt
  getCatalogSubcategories: () => request('/superadmin/catalog/subcategories/'),
  createCatalogSubcategory: (name) => request('/superadmin/catalog/subcategories/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteCatalogSubcategory: (id) => request(`/superadmin/catalog/subcategories/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Usage Tags Mgt
  getCatalogUsageTags: () => request('/superadmin/catalog/usage-tags/'),
  createCatalogUsageTag: (name) => request('/superadmin/catalog/usage-tags/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteCatalogUsageTag: (id) => request(`/superadmin/catalog/usage-tags/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Collection Methods Mgt
  getCatalogCollectionMethods: () => request('/superadmin/catalog/collection-methods/'),
  createCatalogCollectionMethod: (name) => request('/superadmin/catalog/collection-methods/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteCatalogCollectionMethod: (id) => request(`/superadmin/catalog/collection-methods/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Offer Types Mgt
  getOfferTypes: () => request('/superadmin/offers/types/'),
  createOfferType: (name) => request('/superadmin/offers/types/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteOfferType: (id) => request(`/superadmin/offers/types/${id}/delete/`, {
    method: 'DELETE'
  }),

  // Offer Categories Mgt
  getOfferCategories: () => request('/superadmin/offers/categories/'),
  createOfferCategory: (name) => request('/superadmin/offers/categories/create/', {
    method: 'POST',
    body: JSON.stringify({ name })
  }),
  deleteOfferCategory: (id) => request(`/superadmin/offers/categories/${id}/delete/`, {
    method: 'DELETE'
  }),
};

// ==================== PLACES API ====================
export const placesAPI = {
  autocomplete: (input) => request(`/patients/autocomplete-address/?input=${encodeURIComponent(input)}`),
  details: (placeId) => request(`/patients/place-details/?place_id=${placeId}`),
};

// Generic request wrapper for default export
const api = {
  get: async (endpoint, options = {}) => {
    const ep = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    const res = await request(ep, { ...options, method: 'GET' });
    return { data: res };
  },
  post: async (endpoint, data, options = {}) => {
    const ep = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    const res = await request(ep, { ...options, method: 'POST', body: JSON.stringify(data) });
    return { data: res };
  },
  put: async (endpoint, data, options = {}) => {
    const ep = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    const res = await request(ep, { ...options, method: 'PUT', body: JSON.stringify(data) });
    return { data: res };
  },
  delete: async (endpoint, options = {}) => {
    const ep = endpoint.startsWith('/api') ? endpoint.slice(4) : endpoint;
    const res = await request(ep, { ...options, method: 'DELETE' });
    return { data: res };
  },
};

export default api;
